import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useNotify } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { SectionCard } from '../shared/Panel';
import { dateTimeNoSeconds } from '../shared/dateTime';
import { useFeature } from '../admin/featureFlags/FeatureGate';
import { useHasPermission } from '../admin/PermissionGate';
import { toErrorMessage } from './preOrderApi';
import {
  FEATURE_PRE_ORDER_ATTACHMENTS,
  MIN_DELETE_REASON_LENGTH,
  deleteAttachment,
  fetchAttachmentBlob,
  fetchAttachments,
  localRefusal,
  uploadAttachment,
  uploadErrorMessage,
  type PreOrderAttachment,
  type PreOrderAttachmentList,
} from './preOrderAttachmentsApi';

/**
 * „Pliki od klienta” przy prezleceniu (decyzja użytkownika 26.10.2026; przełącznik
 * `preOrderAttachments`, S-26.10-13; reguły w API `pre-order/pre-order-attachments.ts`).
 *
 * Na karcie prezlecenia (obok źródła) i w oknie kontroli Administracji. CS
 * albo Administracja dołącza plik, z którego wpisała prezlecenie; PDF i obraz
 * otwierają się na miejscu, reszta pobiera się na dysk. Usunąć można przed
 * akceptacją, z powodem. Bez przyjęcia zleceń (FTL, Trade, gość) — sama lista,
 * bez otwierania plików (API i tak odmawia 403).
 */

/** Podgląd PDF albo obrazu z bloba (token idzie nagłówkiem, nie w adresie). */
const InlinePreview = ({ preOrderId, att }: { preOrderId: number; att: PreOrderAttachment }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let objectUrl: string | null = null;
    let alive = true;
    fetchAttachmentBlob(preOrderId, att.id)
      .then((blob) => {
        if (!alive) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [preOrderId, att.id]);
  if (failed) return <Alert severity="error">Nie udało się otworzyć pliku.</Alert>;
  if (!url) return <CircularProgress size={20} />;
  return (
    <Box data-testid="pre-order-attachment-viewer" data-attachment-id={att.id}>
      {att.kind === 'image' ? (
        <img src={url} alt={att.filename} style={{ maxWidth: '100%', maxHeight: 620, border: '1px solid #ddd' }} />
      ) : (
        <iframe src={url} title={att.filename} style={{ width: '100%', height: 620, border: '1px solid #ddd' }} />
      )}
    </Box>
  );
};

/** Pobranie na dysk z nazwą pliku. */
async function saveToDisk(preOrderId: number, att: PreOrderAttachment): Promise<void> {
  const blob = await fetchAttachmentBlob(preOrderId, att.id);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = att.filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const DeleteForm = ({
  busy,
  onConfirm,
  onCancel,
}: {
  busy: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}) => {
  const [reason, setReason] = useState('');
  const enough = reason.trim().length >= MIN_DELETE_REASON_LENGTH;
  return (
    <Stack spacing={1} sx={{ mt: 1 }}>
      <TextField
        label="Powód usunięcia (wymagany)"
        size="small"
        fullWidth
        multiline
        minRows={2}
        value={reason}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setReason(e.target.value)}
        helperText={`Co najmniej ${MIN_DELETE_REASON_LENGTH} znaków — zostaje w historii prezlecenia.`}
        slotProps={{ htmlInput: { 'data-testid': 'pre-order-attachment-delete-reason' } }}
      />
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Button
          size="small"
          variant="contained"
          color="error"
          disabled={busy || !enough}
          onClick={() => onConfirm(reason.trim())}
          data-testid="pre-order-attachment-delete-confirm"
        >
          Usuń plik
        </Button>
        <Button size="small" disabled={busy} onClick={onCancel}>
          Anuluj
        </Button>
      </Stack>
    </Stack>
  );
};

export const PreOrderAttachments = ({
  preOrderId,
  onChanged,
}: {
  preOrderId: number;
  /** Po dodaniu albo usunięciu — np. odświeżenie historii na karcie. */
  onChanged?: () => void;
}) => {
  const on = useFeature(FEATURE_PRE_ORDER_ATTACHMENTS);
  if (!on) return null;
  return <AttachmentsSection preOrderId={preOrderId} onChanged={onChanged} />;
};

const AttachmentsSection = ({ preOrderId, onChanged }: { preOrderId: number; onChanged?: () => void }) => {
  const notify = useNotify();
  const canWork = useHasPermission('ORDER_INTAKE');
  const input = useRef<HTMLInputElement | null>(null);
  const [list, setList] = useState<PreOrderAttachmentList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      setList(await fetchAttachments(preOrderId));
      setError(null);
    } catch (e) {
      setError(toErrorMessage(e));
    }
  }, [preOrderId]);

  useEffect(() => {
    setList(null);
    void load();
  }, [load]);

  const live = list?.attachments.filter((a) => a.deletedAt === null) ?? [];
  const removed = list?.attachments.filter((a) => a.deletedAt !== null) ?? [];

  const onPick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !list) return;
    const refusal = localRefusal(file, list.limits);
    if (refusal) {
      notify(refusal, { type: 'error' });
      return;
    }
    setBusy(true);
    try {
      const saved = await uploadAttachment(preOrderId, file);
      notify(`Plik „${saved.filename}” dołączony do prezlecenia`, { type: 'success' });
      await load();
      onChanged?.();
    } catch (err) {
      notify(uploadErrorMessage(err, toErrorMessage), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (att: PreOrderAttachment, reason: string) => {
    setBusy(true);
    try {
      await deleteAttachment(preOrderId, att.id, reason);
      notify(`Plik „${att.filename}” usunięty — wpis zostaje w historii`, { type: 'success' });
      setDeleting(null);
      if (viewing === att.id) setViewing(null);
      await load();
      onChanged?.();
    } catch (err) {
      notify(toErrorMessage(err), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const onDownload = async (att: PreOrderAttachment) => {
    try {
      await saveToDisk(preOrderId, att);
    } catch (err) {
      notify(toErrorMessage(err), { type: 'error' });
    }
  };

  const uploadBlocked = list
    ? (list.uploadRefusal ??
      (list.storageAvailable ? null : 'Przechowywanie plików nie jest skonfigurowane na tym środowisku.'))
    : null;

  return (
    <SectionCard title={`Pliki od klienta (${live.length})`}>
      <Stack spacing={1} data-testid="pre-order-attachments" data-count={live.length}>
        {error ? <Alert severity="error">{error}</Alert> : null}
        {!list && !error ? <CircularProgress size={20} /> : null}
        {list && live.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Brak plików. {canWork ? 'Dołącz zamówienie albo inny dokument, z którego wpisano prezlecenie.' : ''}
          </Typography>
        ) : null}
        {live.map((a) => (
          <Box
            key={a.id}
            data-testid="pre-order-attachment-row"
            data-attachment-id={a.id}
            sx={{ borderTop: '1px solid #eee', pt: 1 }}
          >
            <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                {a.filename}
              </Typography>
              <Chip size="small" variant="outlined" label={`${a.kindLabel}, ${a.sizeLabel}`} />
              <Typography variant="caption" color="text.secondary">
                {[a.uploadedBy, dateTimeNoSeconds(a.createdAt)].filter(Boolean).join(' · ')}
              </Typography>
            </Stack>
            {canWork ? (
              <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                {a.inline ? (
                  <Button
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => setViewing(viewing === a.id ? null : a.id)}
                    data-testid="pre-order-attachment-preview"
                  >
                    {viewing === a.id ? 'Zwiń podgląd' : 'Podgląd'}
                  </Button>
                ) : null}
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => void onDownload(a)}
                  data-testid="pre-order-attachment-download"
                >
                  Pobierz
                </Button>
                {list?.deleteRefusal === null && deleting !== a.id ? (
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                    disabled={busy}
                    onClick={() => setDeleting(a.id)}
                    data-testid="pre-order-attachment-delete"
                  >
                    Usuń
                  </Button>
                ) : null}
              </Stack>
            ) : null}
            {deleting === a.id ? (
              <DeleteForm busy={busy} onConfirm={(r) => void onDelete(a, r)} onCancel={() => setDeleting(null)} />
            ) : null}
            {viewing === a.id && canWork ? (
              <Box sx={{ mt: 1 }}>
                <InlinePreview preOrderId={preOrderId} att={a} />
              </Box>
            ) : null}
          </Box>
        ))}
        {canWork && list ? (
          <Box>
            <input
              ref={input}
              type="file"
              hidden
              accept={list.limits.extensions.map((e) => `.${e}`).join(',')}
              onChange={(e) => void onPick(e)}
              data-testid="pre-order-attachment-input"
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={busy ? <CircularProgress size={16} /> : <AttachFileIcon />}
              disabled={busy || uploadBlocked !== null}
              onClick={() => input.current?.click()}
              data-testid="pre-order-attachment-upload"
            >
              Dołącz plik od klienta
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {uploadBlocked ??
                `PDF, arkusz, dokument, obraz albo zapisana wiadomość — do ${Math.round(list.limits.maxBytes / (1024 * 1024))} MB, najwyżej ${list.limits.maxCount} plików. PDF i obraz otworzysz na miejscu, resztę pobierzesz.`}
            </Typography>
          </Box>
        ) : null}
        {canWork && list?.deleteRefusal && live.length > 0 ? (
          <Typography variant="caption" color="text.secondary" data-testid="pre-order-attachment-delete-locked">
            {list.deleteRefusal}
          </Typography>
        ) : null}
        {removed.length > 0 ? (
          <Box component="details" data-testid="pre-order-attachments-removed">
            <summary>
              <Typography variant="caption" component="span">
                Usunięte ({removed.length})
              </Typography>
            </summary>
            {removed.map((a) => (
              <Typography key={a.id} variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                <s>{a.filename}</s> — usunął {a.deletedBy ?? '—'}, {dateTimeNoSeconds(a.deletedAt as string)}; powód:{' '}
                {a.deletedReason ?? '—'}
              </Typography>
            ))}
          </Box>
        ) : null}
      </Stack>
    </SectionCard>
  );
};
